import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { isAxiosError } from 'axios';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import type {
  ChecklistResource,
  ChecklistTemplate,
  ChecklistRunDetail,
  ChecklistRunStatus,
  ChecklistRunSummary,
} from '@/features/checklists/types';
import { parseStreamRow, queryStream, type StreamRowPayload } from '@/features/workspaces/stream';
import { matchesChecklistSnapshot, withSortedItems } from '@/features/checklists/utils';

export const CHECKLIST_TEMPLATES_QUERY_KEY = 'checklistTemplates';

export const checklistTemplatesQuery = (organizationId: string, language: string) =>
  queryOptions({
    queryKey: [CHECKLIST_TEMPLATES_QUERY_KEY, organizationId, language],
    queryFn: async () =>
      (
        await api.get<ChecklistTemplate[]>('v1/checklists/templates', {
          params: { organizationId, language },
        })
      ).data,
  });

const CHECKLIST_RUNS_QUERY_KEY = 'checklistRuns';
const CHECKLIST_RUN_QUERY_KEY = 'checklistRun';

const checklistQueryKey = ({ documentType, resourceId }: ChecklistResource, runId?: string) =>
  runId
    ? [CHECKLIST_RUN_QUERY_KEY, documentType, resourceId, runId]
    : [CHECKLIST_RUNS_QUERY_KEY, documentType, resourceId];

const checklistStream = (resource: ChecklistResource, runId?: string) => {
  const { documentType, resourceId } = resource;
  const queryKey = checklistQueryKey(resource, runId);
  return queryStream<
    ChecklistRunSummary[] | ChecklistRunDetail,
    StreamRowPayload<ChecklistRunStatus>
  >({
    topic: 'checklist_run',
    type: DOCUMENT_TYPE[documentType],
    resourceId,
    isActive: (data) =>
      Array.isArray(data) ? data.some(isChecklistRunActive) : !!data && isChecklistRunActive(data),
    onStatusUpdate: (payload, queryClient) => {
      const current = queryClient.getQueryData<ChecklistRunSummary[] | ChecklistRunDetail>(
        queryKey,
      );
      const runs = Array.isArray(current) ? current : current && [current];
      if ('rows' in payload && Array.isArray(payload.rows)) {
        const rows = payload.rows.filter((row) =>
          runId ? row?.run_id === runId : row?.work_package_id == null,
        );
        if (matchesChecklistSnapshot(rows, runs)) return;
      }

      const row = parseStreamRow(payload);
      if (!row) return;

      queryClient.invalidateQueries({
        queryKey: [CHECKLIST_RUNS_QUERY_KEY, documentType, resourceId],
        type: 'inactive',
      });
      queryClient.invalidateQueries({
        queryKey: [CHECKLIST_RUN_QUERY_KEY, documentType, resourceId],
        type: 'inactive',
      });
      if (runId && row !== 'refetch' && row.rowId !== runId) return;
      if (
        runId ||
        row === 'refetch' ||
        !runs?.some((run) => run.id === row.rowId) ||
        !isChecklistRunActive(row)
      )
        return queryClient.invalidateQueries({ queryKey });

      queryClient.setQueryData<ChecklistRunSummary[]>(queryKey, (old) =>
        old?.map((run) =>
          run.id === row.rowId
            ? { ...run, status: row.status, progressPercent: row.progress }
            : run,
        ),
      );
    },
  });
};

export const checklistRunsQuery = (resource: ChecklistResource) =>
  queryOptions({
    queryKey: checklistQueryKey(resource),
    queryFn: async () =>
      (
        await api.get<ChecklistRunSummary[]>('v1/checklists/runs', {
          params: { type: DOCUMENT_TYPE[resource.documentType], resourceId: resource.resourceId },
        })
      ).data,
    meta: { stream: checklistStream(resource) },
  });

export const checklistRunQuery = (resource: ChecklistResource, runId: string) =>
  queryOptions({
    queryKey: checklistQueryKey(resource, runId),
    queryFn: async () =>
      withSortedItems(
        (
          await api.get<ChecklistRunDetail>(`v1/checklists/runs/${runId}`, {
            params: { type: DOCUMENT_TYPE[resource.documentType], resourceId: resource.resourceId },
          })
        ).data,
      ),
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 3;
    },
    meta: { stream: checklistStream(resource, runId) },
  });

export const isChecklistRunActive = (run: { status: ChecklistRunStatus }) =>
  run.status === 'queued' || run.status === 'running';
